/**
 * space_squad_nav.ts — squadron orders that know where the mate is, and a match-speed that knows which hull you fly
 * (space campaign B, 2026-09-30; report §10.1, space.md §14 gap 24). Pure.
 *
 * THE SQUAD LAW. The ops roster used to read a mate's raw `player_state` position — whatever sky they were in, or the
 * ground — and `Warp to` flew to those coordinates in THIS system. A mate's `space_location` (mode, system) and how
 * fresh the observation is now resolve into EXACTLY ONE legal offer:
 *   · same system, in flight, observed within SQUAD_STALE_S → INTERCEPT (the in-system hyper to their position);
 *   · in flight in another CHARTED system → TRAVEL: "Travel to <system>" through the existing travel order (the
 *     server's `requestTravel` hyper — it refuses when docked, in transit or tackled), behind its own confirm, and you
 *     arrive at that system's arrival point, not at the mate;
 *   · offline, not in space, docked, in transit, stale or uncharted → a STATUS line and no warp button.
 * The panel renders the result; it decides nothing.
 *
 * THE MATCH LAW. `throttle = |v_target| / 105` (the Lancer's top) ignored the hull you fly and the power routing, and
 * a target at ≥ 103 m/s set the throttle past the 0.98 overcruise gate, so "matching" wound the engines to ×10 and
 * ran away. The throttle is now the target's (validated) speed over THIS hull's top at its routing, capped just under
 * the overcruise gate.
 */

/** A mate whose newest observation is older than this is not warped to (the LOCAL roster's 120 s cut-off). */
export const SQUAD_STALE_S = 120;

export interface MateFacts {
  name: string;
  /** presence.online — the identity has a live connection (THE CONNECTION LAW). */
  online: boolean;
  /** A space_session row exists (in the sector, in any system). */
  inSpace: boolean;
  /** space_location (null while in space = the lazily-created sys0 / flight row). */
  loc: { systemId: string; mode: string; stationKey: string } | null;
  /** The destination of an order in transit, if any. */
  transitTo: string | null;
  /** The mate's streamed position (player_state), if any. */
  pos: { x: number; y: number; z: number } | null;
  /** Seconds since the newest observation of the mate (player_state / space_presence), null = never. */
  observedAgeS: number | null;
}

export interface SquadMe { systemId: string; x: number; y: number; z: number }

export type SquadOffer =
  | { kind: 'intercept'; systemId: string; x: number; y: number; z: number; distKm: number; text: string }
  | { kind: 'travel'; systemId: string; systemName: string; text: string; confirm: string }
  | { kind: 'status'; text: string };

/** "12 s" / "4 min" / "3 h" — a compact age. */
export function ageText(s: number): string {
  if (!Number.isFinite(s) || s < 0) return 'unknown';
  if (s < 90) return `${Math.round(s)} s`;
  if (s < 5400) return `${Math.round(s / 60)} min`;
  return `${Math.round(s / 3600)} h`;
}

/**
 * The one offer for a mate. `systemName(id)` names a charted system the travel order can reach (null = uncharted);
 * `stationName(key)` names a station key.
 */
export function squadOffer(
  me: SquadMe,
  mate: MateFacts,
  systemName: (id: string) => string | null,
  stationName: (key: string) => string | null = () => null,
): SquadOffer {
  if (!mate.online) return { kind: 'status', text: 'offline' };
  if (!mate.inSpace) return { kind: 'status', text: 'on the ground — not in space (no warp)' };
  const loc = mate.loc ?? { systemId: 'sys0', mode: 'flight', stationKey: '' };
  const where = systemName(loc.systemId) ?? loc.systemId;
  if (loc.mode === 'warp' || loc.mode === 'course') {
    const to = mate.transitTo ? (systemName(mate.transitTo) ?? mate.transitTo) : 'another system';
    return { kind: 'status', text: `in transit to ${to} — wait for the arrival` };
  }
  if (loc.mode === 'docked') {
    const st = loc.stationKey ? (stationName(loc.stationKey) ?? loc.stationKey) : 'a station';
    return { kind: 'status', text: `docked at ${st} in ${where}` };
  }
  if (loc.mode !== 'flight') return { kind: 'status', text: `in ${where} (${loc.mode})` };
  if (mate.observedAgeS === null || !(mate.observedAgeS <= SQUAD_STALE_S)) {
    return { kind: 'status', text: `last seen ${mate.observedAgeS === null ? 'never' : `${ageText(mate.observedAgeS)} ago`} in ${where} — too old to warp to` };
  }
  if (loc.systemId === me.systemId) {
    const p = mate.pos;
    if (!p || ![p.x, p.y, p.z].every(Number.isFinite)) return { kind: 'status', text: `in ${where} — position unknown` };
    const distKm = Math.round(Math.hypot(p.x - me.x, p.y - me.y, p.z - me.z) / 1000);
    return { kind: 'intercept', systemId: loc.systemId, x: p.x, y: p.y, z: p.z, distKm, text: `in this system · ${distKm} km · seen ${ageText(mate.observedAgeS)} ago` };
  }
  const name = systemName(loc.systemId);
  if (!name) return { kind: 'status', text: `in an uncharted sky (${loc.systemId}) — no route` };
  return {
    kind: 'travel', systemId: loc.systemId, systemName: name,
    text: `in ${name} · seen ${ageText(mate.observedAgeS)} ago`,
    confirm: `Hyper-warp to ${name}: a 30 s tunnel, no fee. You arrive at ${name}'s arrival point — not at ${mate.name}; fly to them from there. Refused while docked, in transit or tackled.`,
  };
}

// ── THE MATCH LAW ───────────────────────────────────────────────────────────────────────────────────────
/** SpaceModule winds overcruise up while the throttle is above this (`this.throttle > 0.98`). */
export const OVERCRUISE_GATE = 0.98;
/** Matching never asks for more than this — the engines must not wind up while matching. */
export const MATCH_MAX_THROTTLE = 0.97;
/** A target reported faster than this is in overcruise / a jump, or the report is bad: no match. */
export const MATCH_TARGET_MAX_MPS = 2000;

/**
 * The throttle that flies the target's speed on THIS hull. `hullTop` = the hull's speed at throttle 1 with no boost
 * or overcruise (stats.maxSpeed × the power routing's speed factor). null = cannot match (a bad or absurd report).
 */
export function matchSpeedThrottle(targetSpeed: number, hullTop: number): { throttle: number; capped: boolean } | null {
  if (!Number.isFinite(targetSpeed) || targetSpeed < 0 || targetSpeed > MATCH_TARGET_MAX_MPS) return null;
  if (!Number.isFinite(hullTop) || hullTop <= 0) return null;
  const want = targetSpeed / hullTop;
  return { throttle: Math.min(MATCH_MAX_THROTTLE, Math.max(0, want)), capped: want > MATCH_MAX_THROTTLE };
}
